import React from 'react';

import styled from '@mint-lib/styled';
import AccordionM, {
  AccordionProps as AccordionPropsM,
} from '@mui/material/Accordion';

export interface AccordionProps extends AccordionPropsM {
  /**
   * The content of the component.

   */
  children: AccordionPropsM['children'];
  /**
   * Override or extend the styles applied to the component.
 
   */
  classes?: AccordionPropsM['classes'];

  /**
   * If true, expands the accordion by default.
  
   */
  defaultExpanded?: boolean;

  /**
   * If true, the component is disabled.
   */
  disabled?: boolean;

  /**
   * If true, it removes the margin between two expanded accordion items and the increase of height.
   */
  disableGutters?: boolean;

  /**
   * If true, expands the accordion, otherwise collapse it. Setting this prop enables control over the accordion.
   **/
  expanded?: boolean;

  /**
   * Callback fired when the expand/collapse state is changed.
   */
  onChange?: AccordionPropsM['onChange'];

  /**
   * If true, rounded corners are disabled.
   */
  square?: boolean;

  /**
   * The component used for the transition. Follow this guide to learn more about the requirements for this component.
   */
  TransitionComponent?: AccordionPropsM['TransitionComponent'];

  /**
   * Props applied to the transition element. By default, the element is based on this Transition component.
   */
  TransitionProps?: AccordionPropsM['TransitionProps'];

  sx?: AccordionPropsM['sx'];
  /**
   * variant of the accordion
   */
  color?: 'primary' | 'secondary';
}

/**
 * @deprecated Use the new Accordion component from @mint-lib/tw-components instead.
 */
function Accordion({ expanded, color = 'primary', ...props }: AccordionProps) {
  const [newExpanded, setNewExpanded] = React.useState<boolean>(false);

  React.useEffect(() => {
    setNewExpanded(expanded as boolean);
  }, [expanded]); // Only re-run the effect if value prop changes

  return (
    <StyledAccordion
      {...props}
      expanded={newExpanded}
      $color={color}
      square={true}
      disabled={props.disabled}
    >
      {props.children}
    </StyledAccordion>
  );
}

export default Accordion;

const StyledAccordion = styled(AccordionM, {
  shouldForwardProp: (prop) => prop !== '$color',
})<{
  $color: 'primary' | 'secondary';
}>(({ theme, $color, disabled }) => ({
  '&.MuiAccordion-root:before': {
    display: 'none',
  },
  '&.MuiPaper-root.MuiAccordion-root.Mui-expanded': {
    margin: '0px',
  },
  '.MuiAccordionSummary-root': {
    '&:hover': {
      backgroundColor:
        $color === 'primary'
          ? theme.palette.ui.secondary
          : theme.palette.ui.hover,
    },
  },
  borderRadius: '8px',
  border:
    $color === 'primary' ? `1px solid ${theme.palette.border.primary}` : 'none',
  boxShadow: 'none',
  backgroundColor:
    $color === 'primary'
      ? theme.palette.ui.primary
      : theme.palette.ui.secondary,
}));
